import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getChurchProfile, updateChurchProfile, uploadSignatorySignature, deleteSignatorySignature, sendDueRemindersNow } from '../../services/api';
import AuthImg from '../../components/AuthImg';
import Toast from '../../components/Toast';

// Order matches printed CR/PV: Prepared (left) | Approved+stamp (centre) |
// Received (right). Approved sits centre because the church stamp naturally
// falls on the approver's signature block.
const SIG_SLOTS = [
  { slot: 'prepared', label: 'Prepared By' },
  { slot: 'approved', label: 'Approved By' },
  { slot: 'received', label: 'Received By' },
];

const ChurchProfile = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState({ church_name: '', church_address: '', church_phone: '', church_email: '', currency: 'ETB', currencies: ['$', 'K'], hagest_sbket: '', city: '', default_fx_rate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4500); };

  // v1.9.32 — manually fire the due-contribution push reminders now.
  // Skips members who have nothing owed (server-side HAVING clause).
  const handleSendRemindersNow = async () => {
    if (!window.confirm('Send overdue-contribution reminders now to every member with an unpaid balance? Members with nothing owed are skipped.')) return;
    setSendingReminders(true);
    try {
      const r = await sendDueRemindersNow();
      const { members = 0, pushed = 0 } = r.data || {};
      showToast(members === 0
        ? 'No members with overdue contributions right now — nobody was notified.'
        : `Sent to ${members} member${members !== 1 ? 's' : ''} (${pushed} device push${pushed !== 1 ? 'es' : ''}). Anyone with nothing owed was skipped.`);
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not send reminders', 'error');
    } finally { setSendingReminders(false); }
  };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const reload = () => getChurchProfile().then(r => {
    const d = r.data || {};
    const currencies = Array.isArray(d.currencies) && d.currencies.length ? d.currencies : ['$', 'K'];
    setForm(prev => ({ ...prev, ...d, currencies }));
  }).catch(() => {});

  useEffect(() => { reload(); }, []);

  const handleSave = async () => {
    setError('');
    if (!form.church_name) return setError('Church name is required');
    const cleanedCurrencies = (form.currencies || []).map(s => String(s || '').trim()).filter((s, i, a) => s && a.indexOf(s) === i);
    if (cleanedCurrencies.length === 0) return setError('At least one currency is required');
    setSaving(true);
    try {
      await updateChurchProfile({ ...form, currencies: cleanedCurrencies });
      setForm(p => ({ ...p, currencies: cleanedCurrencies }));
      showToast('Profile saved');
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.churchProfile')}</h1></div>
      </div>

      {/* v1.9.2 — tabs. Details = the original form. Signatories = fixed
          names + signature images used on every printed CR / PV footer. */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 16, maxWidth: 600 }}>
        <button
          className="btn btn-sm"
          onClick={() => setTab('details')}
          style={{ borderRadius: '8px 8px 0 0', padding: '10px 18px', border: 'none', background: tab === 'details' ? '#4f46e5' : 'transparent', color: tab === 'details' ? '#fff' : '#374151', fontWeight: 600 }}
        >
          {t('churchProfile.tabDetails', { defaultValue: 'Details' })}
        </button>
        <button
          className="btn btn-sm"
          onClick={() => setTab('signatories')}
          style={{ borderRadius: '8px 8px 0 0', padding: '10px 18px', border: 'none', background: tab === 'signatories' ? '#4f46e5' : 'transparent', color: tab === 'signatories' ? '#fff' : '#374151', fontWeight: 600 }}
        >
          {t('churchProfile.tabSignatories', { defaultValue: 'Signatories' })}
        </button>
      </div>

      {tab === 'signatories' && (
        <SignatoriesTab form={form} setForm={setForm} showToast={showToast} reload={reload} saving={saving} setSaving={setSaving} handleSave={handleSave} />
      )}

      {tab === 'details' && (
      <div className="card" style={{ maxWidth: 600 }}>
        <div className="card-body">
          {error && <div className="error-msg">{error}</div>}
          <div className="form-grid">
            <div className="form-group form-full">
              <label>{t('common.name')} <span className="required">*</span></label>
              <input value={form.church_name} onChange={set('church_name')} />
            </div>
            <div className="form-group form-full">
              <label>{t('common.address')}</label>
              <input value={form.church_address} onChange={set('church_address')} />
            </div>
            <div className="form-group">
              <label>{t('common.phone')}</label>
              <input value={form.church_phone} onChange={set('church_phone')} />
            </div>
            <div className="form-group">
              <label>{t('common.email')}</label>
              <input type="email" value={form.church_email} onChange={set('church_email')} />
            </div>
            <div className="form-group form-full">
              <label>Currencies <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>(add as many as you need — first one is the default)</span></label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.currencies.map((sym, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#9ca3af', minWidth: 50 }}>{i === 0 ? 'Primary' : `#${i + 1}`}</span>
                    <input
                      value={sym}
                      onChange={e => setForm(p => ({ ...p, currencies: p.currencies.map((s, j) => j === i ? e.target.value : s) }))}
                      placeholder="e.g. $, K, ETB"
                      maxLength={6}
                      style={{ flex: '0 0 120px' }}
                    />
                    {form.currencies.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => setForm(p => ({ ...p, currencies: p.currencies.filter((_, j) => j !== i) }))}
                        title="Remove this currency"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setForm(p => ({ ...p, currencies: [...p.currencies, ''] }))}
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  + Add currency
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>{t('finance.diocese')}</label>
              <input value={form.hagest_sbket} onChange={set('hagest_sbket')} />
            </div>
            <div className="form-group">
              <label>{t('finance.city')}</label>
              <input value={form.city} onChange={set('city')} />
            </div>
            {/* v1.9.46 — Default FX rate used by the member Submit Payment page
                when a member ticks "Also pay my $ pledge in K". Member can
                override per payment; this is just the pre-filled default. */}
            <div className="form-group">
              <label>Default FX rate (K per $)</label>
              <input
                type="number"
                step="0.01"
                min={0}
                value={form.default_fx_rate || ''}
                onChange={set('default_fx_rate')}
                placeholder="24.00"
              />
              <small style={{ color: '#6b7280', fontSize: 11 }}>
                Used by "Also pay my $ pledge in K" on Submit Payment. Members can override per payment.
              </small>
            </div>
            {/* v1.9.90 — editable sign-off line appended to every WhatsApp
                subscription reminder sent from the Subscription Status page.
                Blank falls back to "Admin, Magabe Haymanot Teklom". */}
            <div className="form-group form-full">
              <label>WhatsApp reminder signature</label>
              <input
                value={form.reminder_signature || ''}
                onChange={set('reminder_signature')}
                placeholder="Admin, Magabe Haymanot Teklom"
              />
              <small style={{ color: '#6b7280', fontSize: 11 }}>
                Shown at the end of every subscription reminder sent via WhatsApp. Leave blank for the default.
              </small>
            </div>
            {/* v1.6.12 — daily due-contribution reminder scheduling. Times
                are interpreted in the configured timezone so '8' means 8 AM
                local church time, not the VPS's UTC clock. */}
            <div className="form-group form-full" style={{ marginTop: 4, padding: '14px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: '#1e40af', fontSize: 13, marginBottom: 8 }}>
                {t('churchProfile.notificationSettings', { defaultValue: 'Notification Settings' })}
              </div>
              <div className="form-grid" style={{ gap: 10 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('churchProfile.timezone', { defaultValue: 'Time zone' })}</label>
                  <input
                    value={form.due_reminder_timezone || ''}
                    onChange={set('due_reminder_timezone')}
                    placeholder="Africa/Lusaka"
                  />
                  <small style={{ color: '#6b7280', fontSize: 11 }}>
                    {t('churchProfile.timezoneHint', { defaultValue: 'IANA timezone name (e.g. Africa/Lusaka, Africa/Addis_Ababa, UTC). Defaults to Africa/Lusaka.' })}
                  </small>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('churchProfile.reminderHours', { defaultValue: 'Reminder hours' })}</label>
                  <input
                    value={form.due_reminder_hours || ''}
                    onChange={set('due_reminder_hours')}
                    placeholder="8"
                  />
                  <small style={{ color: '#6b7280', fontSize: 11 }}>
                    {t('churchProfile.reminderHoursHint', { defaultValue: '24h format, comma-separated. "8" = once at 8 AM. "8, 20" = twice a day. Defaults to 8.' })}
                  </small>
                </div>
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: '#1e40af' }}>
                  Skip the schedule and push a reminder right now to every member with an unpaid overdue balance. Anyone with nothing owed is automatically skipped.
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendRemindersNow}
                  disabled={sendingReminders}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {sendingReminders ? 'Sending…' : '⏰ Send reminders now'}
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
      )}
      {tab === 'details' && <HelperTools onError={(m) => showToast(m, 'error')} onOk={(m) => showToast(m)} />}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

// ── Signatories tab (v1.9.2) ────────────────────────────────────────────
// Fixed 3-slot block that prints under every CR / PV. Names + titles live
// in `settings` as key-value pairs; signature images upload to the settings
// signatory endpoint, path stored as `signatory_<slot>_signature`.
const SignatoriesTab = ({ form, setForm, showToast, reload, saving, setSaving, handleSave }) => {
  const fileRefs = useRef({});
  if (!fileRefs.current.prepared) SIG_SLOTS.forEach(s => { fileRefs.current[s.slot] = React.createRef(); });

  // Auto-uppercase names/titles so the printed receipt matches the rest of
  // the app's convention (member names, CR fields, etc. all render UPPER).
  const setField = (slot, key) => (e) => setForm(p => ({ ...p, [`signatory_${slot}_${key}`]: (e.target.value || '').toUpperCase() }));
  const pick = (slot) => fileRefs.current[slot].current?.click();

  const onFile = (slot) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await uploadSignatorySignature(slot, file);
      showToast('Signature uploaded');
      await reload();
    } catch (err) {
      showToast(err.response?.data?.error || 'Upload failed', 'error');
    }
  };

  const clearSig = async (slot) => {
    if (!window.confirm('Remove this signature?')) return;
    try {
      await deleteSignatorySignature(slot);
      showToast('Signature removed');
      await reload();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed', 'error');
    }
  };

  return (
    <div className="card" style={{ maxWidth: 900 }}>
      <div className="card-body">
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#1e40af' }}>
          <strong>Fixed signatories.</strong> These names and signatures print on every Cash Receipt and Payment Voucher. Upload a signature image once and it is embedded — no physical signature needed.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {SIG_SLOTS.map(({ slot, label }) => {
            const sigPath = form[`signatory_${slot}_signature`];
            return (
              <div key={slot} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fafafa' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#6b7280', marginBottom: 8, fontWeight: 700 }}>{label}</div>
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11 }}>Name</label>
                  <input value={form[`signatory_${slot}_name`] || ''} onChange={setField(slot, 'name')} placeholder="e.g. Zerabruk Tesfay" />
                </div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11 }}>Title <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                  <input value={form[`signatory_${slot}_title`] || ''} onChange={setField(slot, 'title')} placeholder="e.g. Accountant" />
                </div>
                <div style={{ minHeight: 90, border: '2px dashed #e5e7eb', borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6, padding: 8 }}>
                  {sigPath ? (
                    <>
                      <AuthImg src={sigPath} alt="signature" style={{ maxWidth: '100%', maxHeight: 70, objectFit: 'contain' }} />
                      <button type="button" className="btn btn-sm" onClick={() => clearSig(slot)} style={{ color: '#dc2626' }}>✕ Clear</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>No signature uploaded</span>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => pick(slot)}>⤒ Upload</button>
                      <span style={{ fontSize: 10, color: '#9ca3af' }}>PNG/JPG · &lt; 1 MB</span>
                    </>
                  )}
                  <input ref={fileRefs.current[slot]} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile(slot)} />
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 20, padding: 14, background: '#fff', border: '1px dashed #d1d5db', borderRadius: 10 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#6b7280', marginBottom: 8 }}>Preview — CR / PV footer</div>
          <div style={{ position: 'relative', minHeight: 90, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, position: 'relative', zIndex: 2 }}>
              {SIG_SLOTS.map(({ slot, label }) => (
                <div key={slot} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ height: 32, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    {form[`signatory_${slot}_signature`]
                      ? <AuthImg src={form[`signatory_${slot}_signature`]} alt="" style={{ maxHeight: 30, maxWidth: '100%' }} />
                      : <span style={{ fontSize: 10, color: '#d1d5db' }}>(signature)</span>}
                  </div>
                  <div style={{ borderTop: '1px solid #374151', margin: '4px 8px 4px' }} />
                  <div style={{ fontSize: 11, fontWeight: 700 }}>{form[`signatory_${slot}_name`] || '—'}</div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>{form[`signatory_${slot}_title`] || ''}</div>
                </div>
              ))}
            </div>
            <img src="/church-stamp.png" alt="" style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', height: 84, opacity: 0.55, pointerEvents: 'none', zIndex: 1 }} />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Signatories'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Helper Tools panel — manages the /phone-entry passcode + on/off ─────
const HelperTools = ({ onError, onOk }) => {
  const [pass, setPass] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getChurchProfile().then(r => {
      const d = r.data || {};
      if (d.phone_helper_passcode != null) setPass(String(d.phone_helper_passcode));
      if (d.phone_helper_enabled != null) setEnabled(String(d.phone_helper_enabled) === '1');
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    if (!/^\d{4,}$/.test(pass)) return onError('Passcode must be at least 4 digits');
    setSaving(true);
    try {
      await updateChurchProfile({
        phone_helper_passcode: pass,
        phone_helper_enabled: enabled ? '1' : '0',
      });
      onOk('Helper tools updated');
    } catch (e) { onError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;
  const url = `${window.location.origin}/phone-entry`;
  return (
    <div className="card" style={{ maxWidth: 600, marginTop: 16 }}>
      <div className="card-body">
        <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>Helper Tools — Phone Entry</h3>
        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          Share the URL + code with a temp helper. They can only search by name and update each member's phone &amp; WhatsApp.
          No other data is reachable.
        </p>
        <div className="form-grid">
          <div className="form-group form-full">
            <label>URL</label>
            <input readOnly value={url} onFocus={(e) => e.target.select()} style={{ background: '#f9fafb', fontFamily: 'monospace' }} />
          </div>
          <div className="form-group">
            <label>Access code</label>
            <input
              type="text"
              inputMode="numeric"
              value={pass}
              onChange={(e) => setPass(e.target.value.replace(/\D/g, ''))}
              placeholder="4 digits"
              style={{ letterSpacing: 4, textAlign: 'center', fontWeight: 700 }}
            />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 14px', borderRadius: 8, background: enabled ? '#f0fdf4' : '#fee2e2', color: enabled ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: 13 }}>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              {enabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Helper Tools'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChurchProfile;
